import { QueryEntity } from "@datorama/akita";
import { combineLatest } from "rxjs";
import { map } from "rxjs/operators";
import { injectable } from "tsyringe";

import ProductWithQuantityRulesEntity from "../entities/ProductWithQuantityRulesEntity";
import { ProductsState, ProductsStore } from "../stores/ProductsStore";
import { ProductsWithQuantityRulesQuery } from "./ProductsWithQuantityRulesQuery";

@injectable()
export class CombinedProductsAndProductsWithQuantityRulesQuery extends QueryEntity<ProductsState> {
    public constructor(
        private readonly productsStore: ProductsStore,
        private readonly productsWithQuantityRulesQuery: ProductsWithQuantityRulesQuery
    ) {
        super(productsStore);
    }

    public selectProductAndVariant(
        productId: number,
        variantId: number | null
    ) {
        const product$ = this.selectAll().pipe(
            map((products) =>
                products.find((product) => product.id === productId)
            )
        );

        const quantityRulesProduct$ =
            this.productsWithQuantityRulesQuery.selectProduct(productId);

        return combineLatest([product$, quantityRulesProduct$]).pipe(
            map(([product, quantityRulesProduct]) => {
                if (!product) {
                    return {
                        product: null,
                        variant: null,
                    };
                }

                let variant = product.variants.find((variant) => {
                    if (!variantId) {
                        return true;
                    }
                    return variant.id === variantId;
                });

                // If we have quantity rules product and variant, update price and compareAtPrice
                if (variant && quantityRulesProduct) {
                    const variantId = variant.id;
                    const quantityRulesVariant =
                        quantityRulesProduct.variants.find(
                            (qrVariant) => qrVariant.id === variantId
                        );

                    if (quantityRulesVariant) {
                        variant = {
                            ...variant,
                            price: {
                                currency: variant.price.currency,
                                amount: quantityRulesVariant.price,
                            },
                            compareAtPrice: quantityRulesVariant.compareAtPrice
                                ? {
                                      currency: variant.price.currency,
                                      amount: quantityRulesVariant.compareAtPrice,
                                  }
                                : null,
                        };
                    }
                }

                return {
                    product: product,
                    variant: variant || null,
                };
            })
        );
    }

    public getAllProducts() {
        const products$ = this.selectAll();
        const quantityRulesProducts$ =
            this.productsWithQuantityRulesQuery.selectAll();

        return combineLatest([products$, quantityRulesProducts$]).pipe(
            map(([products, quantityRulesProducts]) => {
                const quantityRulesMap = new Map<
                    number,
                    ProductWithQuantityRulesEntity
                >();
                quantityRulesProducts.forEach(
                    (qrProduct: ProductWithQuantityRulesEntity) => {
                        quantityRulesMap.set(qrProduct.id, qrProduct);
                    }
                );

                return products.map((product) => {
                    const quantityRulesProduct = quantityRulesMap.get(
                        product.id
                    );

                    if (!quantityRulesProduct) {
                        return product;
                    }

                    const quantityRulesVariantsMap = new Map<
                        number,
                        (typeof quantityRulesProduct.variants)[0]
                    >();
                    quantityRulesProduct.variants.forEach((qrVariant) => {
                        quantityRulesVariantsMap.set(qrVariant.id, qrVariant);
                    });

                    const updatedVariants = product.variants.map((variant) => {
                        const quantityRulesVariant =
                            quantityRulesVariantsMap.get(variant.id);

                        if (!quantityRulesVariant) {
                            return variant;
                        }

                        return {
                            ...variant,
                            price: {
                                currency: variant.price.currency,
                                amount: quantityRulesVariant.price,
                            },
                            compareAtPrice: quantityRulesVariant.compareAtPrice
                                ? {
                                      currency: variant.price.currency,
                                      amount: quantityRulesVariant.compareAtPrice,
                                  }
                                : null,
                        };
                    });

                    return {
                        ...product,
                        variants: updatedVariants,
                    };
                });
            })
        );
    }
}
